/**
 * Server-side grid for the Space-local attributes tab: an AG Grid
 * **server-side row model** over the cross-space `spaceLocalAttributesSearch`
 * query, mirroring `DocumentBrowserContainer`'s split from `DocumentBrowser` —
 * the Apollo-aware piece lives here so `DocumentTypeSettings` itself stays a
 * presentational component that other consumers can render with no
 * `ApolloProvider` in scope, as long as they never switch to this tab.
 *
 * "Value set" stays unfiltered — `SpaceLocalAttributesInput` has no field
 * for it (see `spaceLocalAttributeFilterModel.ts`'s header).
 */
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { AgGrid, Grid } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import {
  apiSpaceLocalAttributeToVaultLocal,
  DOMAIN_FILTER_COL_ID,
  KIND_FILTER_COL_ID,
  NAME_FILTER_COL_ID,
  NAME_FILTER_OPERATORS,
  REQUIREMENT_FILTER_COL_ID,
  REQUIREMENT_FILTER_LABELS,
  SPACE_FILTER_COL_ID,
  SpaceLocalAttributesGraphqlDataSource,
  type SpaceLocalAttributeNode,
  type VaultSpaceLocalAttribute,
} from "#common/graphql/Attribute";
import { domainIdToSlug } from "#common/graphql/Space";
import { FILTERABLE_DATATYPES } from "./constants";
import * as styles from "./styles_vanilla.css";
import { domainName, localSummary } from "./utils";
import type { Space, VaultDomain } from "#common/types/vault";

/** One space-local attribute row, as returned by the cross-space search. */
export type LocalRow = VaultSpaceLocalAttribute & { id: string };

/** Page size — kept in sync with the grid's `cacheBlockSize` -> `first`. */
const PAGE_SIZE = 50;

// Click is handled by the grid's own `onCellClicked`, not this element.
function LocalActionsCell() {
  return (
    <span className={styles.kebabBtn} aria-hidden>
      <Icon type="ellipsis" size="small" className={styles.kebabIconVertical} />
    </span>
  );
}

const DEFAULT_COL_DEF: AgGrid.ColDef<LocalRow> = {
  resizable: true,
  sortable: false,
  filter: false,
  flex: 1,
  minWidth: 120,
};

// Restricted to the operator subset the backend actually allows for
// `nameFilter` (see `spaceLocalAttributeFilterModel.ts`).
const NAME_COL_FILTER: AgGrid.ColDef<LocalRow> = {
  filter: "agTextColumnFilter",
  filterParams: { filterOptions: Object.keys(NAME_FILTER_OPERATORS), maxNumConditions: 1 },
};

// Fixed value list rather than one derived from loaded rows: the SSRM only
// ever holds one page. Uses `FILTERABLE_DATATYPES` (all 7), not the creation
// whitelist `EDITABLE_DATATYPES` (6, no Reference) — the latter would hide
// Reference-typed rows whenever a value got unticked.
const KIND_COL_FILTER: AgGrid.ColDef<LocalRow> = {
  filter: "agSetColumnFilter",
  filterParams: { values: [...FILTERABLE_DATATYPES] },
};

const REQUIREMENT_COL_FILTER: AgGrid.ColDef<LocalRow> = {
  filter: "agSetColumnFilter",
  filterParams: { values: Object.keys(REQUIREMENT_FILTER_LABELS) },
};

const SIDE_BAR: AgGrid.SideBarDef = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
    },
    {
      id: "filters",
      labelDefault: "Filters",
      labelKey: "filters",
      iconKey: "filter",
      toolPanel: "agFiltersToolPanel",
    },
  ],
};

export type SpaceLocalAttributesGridProps = {
  spaces: Space[];
  domains: VaultDomain[];
  onRowClicked: (row: LocalRow) => void;
  onActionsClicked: (row: LocalRow, x: number, y: number) => void;
  /**
   * Bump to force a full server-side refresh (purge the grid's block cache and
   * reset the datasource cursors, then refetch from block 0). Needed after an
   * out-of-band mutation such as a promotion completing: the datasource
   * fetches `no-cache`, so an Apollo cache update won't reach the grid.
   */
  refreshToken?: number;
};

export function SpaceLocalAttributesGrid({
  spaces,
  domains,
  onRowClicked,
  onActionsClicked,
  refreshToken,
}: SpaceLocalAttributesGridProps) {
  const client = useApolloClient();

  const mapNode = useCallback((node: SpaceLocalAttributeNode): LocalRow => {
    const mapped = apiSpaceLocalAttributeToVaultLocal(node);
    return { ...mapped, id: `${mapped.spaceId}_${mapped.local.id}` };
  }, []);

  const lookups = useMemo(
    () => ({
      spaceIdByName: Object.fromEntries(spaces.map((s) => [s.name, s.id])),
      domainSlugByName: Object.fromEntries(domains.map((d) => [d.name, domainIdToSlug(d.id)])),
    }),
    [spaces, domains],
  );

  // `lookups` is intentionally NOT a dependency here: a catalog refresh
  // (renaming a Space, adding one) doesn't need a new datasource instance —
  // that would reset the SSRM store and drop the user's pagination
  // position. It's pushed to the existing instance via `setLookups` below.
  const datasource = useMemo(
    () =>
      new SpaceLocalAttributesGraphqlDataSource<LocalRow>(client, {
        mapNode,
        pageSize: PAGE_SIZE,
        lookups,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lookups updates flow through setLookups, not reconstruction
    [client, mapNode],
  );

  useEffect(() => {
    datasource.setLookups(lookups);
  }, [datasource, lookups]);

  // A bumped `refreshToken` (e.g. a promotion completing) purges the grid's
  // block cache and resets the datasource cursors so block 0 is refetched
  // from the server — the SSRM otherwise keeps serving its cached blocks and
  // never sees the promoted row disappear. Skipped on mount.
  const gridApiRef = useRef<AgGrid.GridApi<LocalRow> | null>(null);
  const refreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (refreshTokenRef.current === refreshToken) return;
    refreshTokenRef.current = refreshToken;
    datasource.refresh();
    gridApiRef.current?.refreshServerSide({ purge: true });
  }, [refreshToken, datasource]);

  const columnDefs = useMemo(() => {
    const defs: AgGrid.ColDef<LocalRow>[] = [
      {
        headerName: "Attribute",
        colId: NAME_FILTER_COL_ID,
        valueGetter: (p) => p.data?.local.name,
        flex: 1.3,
        minWidth: 170,
        // Server-sortable via `attribute_label_*` (DV-497).
        sortable: true,
        ...NAME_COL_FILTER,
      },
      {
        headerName: "Data type",
        colId: KIND_FILTER_COL_ID,
        valueGetter: (p) => p.data?.local.dataType,
        width: 140,
        cellStyle: { color: "#72716e" },
        // Server-sortable via `attribute_value_type_*` (DV-497).
        sortable: true,
        ...KIND_COL_FILTER,
      },
      {
        headerName: "Space",
        colId: SPACE_FILTER_COL_ID,
        field: "spaceName",
        flex: 1.4,
        minWidth: 200,
        // Server-sortable via `space_name_*` (DV-497).
        sortable: true,
        filter: "agSetColumnFilter",
        // Best-effort, not exhaustive: `spaces` is whatever catalog the
        // consumer loaded, while the grid's own SSRM spans every space
        // `spaceLocalAttributesSearch` can return (see the pre-existing note
        // on `spaces.find(...)` in `index.tsx`) — a space can appear in the
        // Space column while missing from this filter's options.
        filterParams: { values: spaces.map((s) => s.name) },
      },
      {
        headerName: "Domain",
        colId: DOMAIN_FILTER_COL_ID,
        valueGetter: (p) => (p.data ? domainName(p.data.domain, domains) : ""),
        width: 140,
        // Server-sortable via `domain_*` (DV-686/DV-687).
        sortable: true,
        filter: "agSetColumnFilter",
        // Same caveat as Space above, though `domains` is a small closed
        // catalog so it's far less likely to be short in practice.
        filterParams: { values: domains.map((d) => d.name) },
      },
      {
        headerName: "Requirement",
        colId: REQUIREMENT_FILTER_COL_ID,
        valueGetter: (p) => (p.data?.local.required ? "Required" : "Optional"),
        width: 130,
        cellStyle: { color: "#72716e" },
        // Server-sortable via `quality_contract_*` (DV-686/DV-687).
        // `quality_contract_asc` is Required-then-Optional (product order), not alphabetical.
        sortable: true,
        ...REQUIREMENT_COL_FILTER,
      },
      {
        headerName: "Value set",
        colId: "valueSet",
        valueGetter: (p) => (p.data ? localSummary(p.data.local) : "—"),
        flex: 1.3,
        minWidth: 170,
        cellStyle: { color: "#72716e" },
      },
      {
        headerName: "",
        colId: "actions",
        // `DEFAULT_COL_DEF` sets `flex: 1, minWidth: 120`; min == max + `flex: 0`
        // is what pins this column at 60 despite those defaults.
        minWidth: 60,
        maxWidth: 60,
        flex: 0,
        resizable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        cellRenderer: LocalActionsCell,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        },
        pinned: "right",
        lockPinned: true,
      },
    ];
    return defs;
  }, [spaces, domains]);

  return (
    <div className={styles.results}>
      <div className={styles.gridWrap}>
        <Grid<LocalRow>
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          getRowId={(p) => p.data.id}
          rowModelType="serverSide"
          serverSideDatasource={datasource}
          cacheBlockSize={PAGE_SIZE}
          maxBlocksInCache={10}
          pagination
          paginationPageSize={PAGE_SIZE}
          sideBar={SIDE_BAR}
          suppressCellFocus
          onGridReady={(e) => {
            gridApiRef.current = e.api;
          }}
          onCellClicked={(e) => {
            if (!e.data) return;
            if (e.column.getColId() === "actions") {
              const ev = e.event as MouseEvent | undefined;
              const cell = (ev?.target as HTMLElement | null)?.closest(".ag-cell");
              const rect = cell?.getBoundingClientRect();
              onActionsClicked(
                e.data,
                rect ? rect.right : (ev?.clientX ?? 0),
                rect ? rect.bottom : (ev?.clientY ?? 0),
              );
            } else {
              onRowClicked(e.data);
            }
          }}
        />
      </div>
    </div>
  );
}
